import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useUser } from '../context/UserContext';

// Stable default so omit/undefined does not allocate a new [] every render
// (that previously re-triggered the options effect in an infinite loop).
const EMPTY_EXCLUDE = [];

/**
 * CountryPicker – reusable country selector component.
 *
 * Props:
 *   value         – currently selected option (react-select format)
 *   onChange      – callback fired with the selected option(s)
 *   placeholder   – input placeholder (optional)
 *   isMulti       – allow multiple selections (optional)
 *   ...props      – any other props are forwarded to react-select
 */
const CountryPicker = ({
  value,
  onChange,
  placeholder = 'Select a country…',
  isMulti = false,
  excludeIso3 = EMPTY_EXCLUDE,
  ...props
}) => {
  const { user } = useUser();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Content keys keep UX identical when callers pass inline arrays like ["ERT"],
  // without re-running the effect on every parent render.
  const excludeKey = Array.isArray(excludeIso3)
    ? excludeIso3.map((c) => String(c).toUpperCase()).sort().join(',')
    : '';
  const countriesKey = Array.isArray(user?.allowedCountriesIso3)
    ? user.allowedCountriesIso3.join(',')
    : '';
  const labelsKey = Array.isArray(user?.allowedCountries)
    ? user.allowedCountries.join('\0')
    : '';

  useEffect(() => {
    if (!user || !user.allowedCountries || !user.allowedCountriesIso3) {
      setLoading(false);
      setOptions((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    // Create options from user's allowed countries
    const countryOptions = user.allowedCountries.map((countryName, index) => ({
      value: user.allowedCountriesIso3[index]?.toLowerCase() || countryName.toLowerCase(),
      label: countryName,
      iso3: user.allowedCountriesIso3[index],
      raw: {
        Country_Name: countryName,
        iso3: user.allowedCountriesIso3[index]
      }
    }));

    // Optionally exclude specific ISO3 codes (case-insensitive)
    const exclusions = excludeKey ? excludeKey.split(',') : [];
    const filteredCountries = countryOptions.filter((opt) => {
      const code = String(opt.iso3 || '').toUpperCase();
      return !exclusions.includes(code);
    });

    // Sort countries alphabetically
    const sortedCountries = filteredCountries.sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    setOptions((prev) => {
      if (
        prev.length === sortedCountries.length &&
        prev.every(
          (opt, i) =>
            opt.iso3 === sortedCountries[i].iso3 &&
            opt.label === sortedCountries[i].label &&
            opt.value === sortedCountries[i].value
        )
      ) {
        return prev;
      }
      return sortedCountries;
    });
    setLoading(false);
    // Content keys (not array/object identity) so Tickets UX stays the same and
    // inline props like excludeIso3={["ERT"]} do not retrigger every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user read via countriesKey/labelsKey
  }, [countriesKey, labelsKey, excludeKey]);

  if (loading) {
    return <div className="text-sm text-gray-500">Loading countries…</div>;
  }

  if (!user || !user.allowedCountries || user.allowedCountries.length === 0) {
    return <div className="text-sm text-gray-500">No countries available for your access level.</div>;
  }

  return (
    <Select
      className="w-full"
      classNamePrefix="country-select"
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isSearchable
      isMulti={isMulti}
      {...props}
    />
  );
};

export default CountryPicker;
